import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showSnackbar } from "../../snackbar/Snackbar.slice";

const BILLING_SETTINGS_CREATE = "billingSettingsForm/create";
const BILLING_SETTINGS_UPDATE = "billingSettingsForm/update";
const BILLING_SETTINGS_GET_BY_MERCHANT_ID = "billingSettingsForm/getByMerchantId";

const API_BASE_URL = process.env.REACT_APP_API_ROUTE;

export const getBillingSettingsById = createAsyncThunk(
  BILLING_SETTINGS_GET_BY_MERCHANT_ID,
  async (payload, { rejectWithValue }) => {
    try {
      const { merchantId, token } = payload;
      const url = `${API_BASE_URL}/merchant-invoice-config/merchant/${merchantId}`;
      
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(url, { headers });
      
      return {
        billingSettingsData: response.data,
        merchantId: merchantId,
      };
    } catch (error) {
      console.error("Error getting billing settings by merchant id thunk:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error retrieving billing settings";
      return rejectWithValue({
        message: errorMessage,
      });
    }
  }
);

export const createBillingSettings = createAsyncThunk(
  BILLING_SETTINGS_CREATE,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const {
        handleGoBack,
        token,
        merchant_id,
        title,
        contact_email,
        contact_phone,
        logo,
        acquirer_logo,
      } = payload;

      const formData = new FormData();
      formData.append("merchant_id", merchant_id);
      if (title) {
        formData.append("title", title);
      }
      formData.append(
        "contact_email",
        contact_email !== undefined && contact_email !== null
          ? String(contact_email)
          : "",
      );
      formData.append(
        "contact_phone",
        contact_phone !== undefined && contact_phone !== null
          ? String(contact_phone)
          : "",
      );
      if (logo && logo instanceof File) {
        formData.append("logo", logo);
      }
      if (acquirer_logo && acquirer_logo instanceof File) {
        formData.append("acquirer_logo", acquirer_logo);
      }

      const url = `${API_BASE_URL}/merchant-invoice-config`;
      const headers = {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type, let the browser set it with boundary for FormData
      };

      const response = await axios.post(url, formData, { headers });
      
      dispatch(
        showSnackbar({
          message: "Billing settings created successfully",
          severity: "success",
        })
      );
      if (handleGoBack) {
        handleGoBack();
      }
      return response.data;
    } catch (error) {
      console.error("Error creating billing settings thunk:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error creating billing settings";
      dispatch(
        showSnackbar({
          message: errorMessage,
          severity: "error",
        })
      );
      return rejectWithValue({
        message: errorMessage,
      });
    }
  }
);

export const updateBillingSettings = createAsyncThunk(
  BILLING_SETTINGS_UPDATE,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const {
        handleGoBack,
        token,
        id,
        merchant_id,
        title,
        contact_email,
        contact_phone,
        logo,
        acquirer_logo,
        logo_action,
        acquirer_logo_action,
      } = payload;

      if (!id) {
        throw new Error("ID is required for updating billing settings");
      }

      const formData = new FormData();
      formData.append("merchant_id", merchant_id);
      if (title) {
        formData.append("title", title);
      }
      formData.append(
        "contact_email",
        contact_email !== undefined && contact_email !== null
          ? String(contact_email)
          : "",
      );
      formData.append(
        "contact_phone",
        contact_phone !== undefined && contact_phone !== null
          ? String(contact_phone)
          : "",
      );
      if (logo && logo instanceof File) {
        formData.append("logo", logo);
      }
      if (acquirer_logo && acquirer_logo instanceof File) {
        formData.append("acquirer_logo", acquirer_logo);
      }
      if (logo_action === "nrop") {
        formData.append("logo_action", "nrop");
      }
      if (acquirer_logo_action === "nrop") {
        formData.append("acquirer_logo_action", "nrop");
      }

      const url = `/merchant-invoice-config/${id}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type, let the browser set it with boundary for FormData
        },
      };

      const response = await axios.put(`${API_BASE_URL}${url}`, formData, config);
      
      // Recargar los datos actualizados para mantener el modo edición
      await dispatch(getBillingSettingsById({ merchantId: merchant_id, token }));
      
      dispatch(
        showSnackbar({
          message: "Billing settings updated successfully",
          severity: "success",
        })
      );
      if (handleGoBack) {
        handleGoBack();
      }
      return response.data;
    } catch (error) {
      console.error("Error updating billing settings thunk:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error updating billing settings";
      dispatch(
        showSnackbar({
          message: errorMessage,
          severity: "error",
        })
      );
      return rejectWithValue({
        message: errorMessage,
      });
    }
  }
);
